import { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import { IllustrationCard } from "./IllustrationCard";
import { api } from "../../lib/api";

type Illustration = {
  id: string;
  title: string;
  file_name: string;
  storage_path: string;
  category: string;
  created_at: string;
  tag_ids: number[];
};

type Category = "works" | "misc" | "other" | "icon";

export const IllustrationList = () => {
  const [illustrations, setIllustrations] = useState<Illustration[]>([]);
  const [selectedCategory, setSelectedCategory] =
    useState<Category>("works");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchIllustrations = useCallback(async (category: Category) => {
    try {
      type Row = Omit<Illustration, "tag_ids"> & { tag_ids: string | number[] | null };
      const data = (await api.getIllustrations()) as Row[];
      const filtered: Illustration[] = data
        .filter((item) => item.category === category)
        .map((item) => ({
          ...item,
          tag_ids:
            typeof item.tag_ids === 'string'
              ? (JSON.parse(item.tag_ids) as number[])
              : (item.tag_ids || []),
        }));
      setIllustrations(filtered);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("イラストの取得に失敗しました");
    }
  }, []);

  useEffect(() => {
    fetchIllustrations(selectedCategory);
  }, [selectedCategory, refreshTrigger, fetchIllustrations]);

  const handleDeleteIllustration = async (illustration: Illustration) => {
    if (!confirm("このイラストを削除してもよろしいですか？")) return;

    try {
      await api.deleteIllustration(illustration.id);
      toast.success("イラストを削除しました");
      setRefreshTrigger((prev: number) => prev + 1);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("削除に失敗しました");
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-2xl font-bold mb-4">アップロード済みイラスト</h3>
        <div className="flex gap-4 mb-4">
          <label className="flex items-center">
            <input
              type="radio"
              value="works"
              checked={selectedCategory === "works"}
              onChange={(e) => setSelectedCategory(e.target.value as Category)}
              className="mr-2"
            />
            作品
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="misc"
              checked={selectedCategory === "misc"}
              onChange={(e) => setSelectedCategory(e.target.value as Category)}
              className="mr-2"
            />
            題材
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="other"
              checked={selectedCategory === "other"}
              onChange={(e) => setSelectedCategory(e.target.value as Category)}
              className="mr-2"
            />
            その他
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="icon"
              checked={selectedCategory === "icon"}
              onChange={(e) => setSelectedCategory(e.target.value as Category)}
              className="mr-2"
            />
            アイコン
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {illustrations.map((illustration: Illustration) => (
          <IllustrationCard
            key={illustration.id}
            illustration={illustration}
            onDelete={handleDeleteIllustration}
            onUpdate={() => setRefreshTrigger((prev: number) => prev + 1)}
          />
        ))}
      </div>
    </div>
  );
};
